import { createClient, SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let initializationError: Error | null = null;

function createSupabaseClient(): SupabaseClient | null {
  if (!supabaseUrl || !supabaseAnonKey) {
    initializationError = new Error(
      "Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file."
    );
    console.error("❌ Supabase Configuration Error:", initializationError.message);
    console.error("📋 Please check your .env file in the project root.");
    return null;
  }
  
  try {
    return createClient(supabaseUrl, supabaseAnonKey);
  } catch (error) {
    initializationError = error as Error;
    console.error("❌ Failed to initialize Supabase client:", error);
    return null;
  }
}

// Lazy initialization - only creates client when accessed
let supabaseInstance: SupabaseClient | null = null;

export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    if (!supabaseInstance) {
      supabaseInstance = createSupabaseClient();
    }
    
    // If initialization failed, return a mock object that throws helpful errors
    if (!supabaseInstance) {
      if (prop === 'auth') {
        return {
          getSession: () => Promise.resolve({ data: { session: null }, error: initializationError }),
          onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
          signInWithPassword: () => Promise.resolve({ data: null, error: initializationError }),
          signUp: () => Promise.resolve({ data: null, error: initializationError }),
          signOut: () => Promise.resolve({ error: initializationError }),
        };
      }
      if (prop === 'from') {
        return () => ({
          select: () => ({
            eq: () => ({
              single: () => Promise.resolve({ data: null, error: initializationError }),
            }),
          }),
          insert: () => Promise.resolve({ data: null, error: initializationError }),
        });
      }
      // For other properties, return a function that throws the error
      return () => {
        throw initializationError || new Error("Supabase client not initialized");
      };
    }
    
    const value = (supabaseInstance as unknown as Record<string, unknown>)[prop as string];
    return typeof value === 'function' ? value.bind(supabaseInstance) : value;
  }
});

// Export error check function
export function getSupabaseError(): Error | null {
  return initializationError;
}
